import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useYM } from 'YM/Context';
import { GoalType } from 'YM/declare';

import { getDevicesPermissionMap } from 'utils/getDevicesPermissionMap';

type CameraGoalType = GoalType.CONFERENCE_PAGE_CAM_PERMISSION | GoalType.CONFERENCE_PAGE_CAM_PERMISSION_SUCCESS;

interface IContext {
    /** Есть разрешение на использование камеры */
    granted: boolean;
    /** Установка разрешения на использование камеры */
    setGrantedState: (state: boolean) => void;
    /** Установка состояния "открыт" модалки с обучением */
    setTutorialState: (state: boolean) => void;
    /** Вызов событий Яндекс метрики связанных с камерой */
    triggerYMCamera: (goal: CameraGoalType) => void;
    /** Состояние "открыт" модалки с обучением */
    tutorialState: boolean;
}

const Context = createContext<IContext>({
    granted: false,
    setGrantedState: () => undefined,
    setTutorialState: () => undefined,
    triggerYMCamera: () => undefined,
    tutorialState: false,
});

export const useCameraContext = () => useContext(Context);

const CameraPermissionContext: FC<PropsWithChildren> = ({ children }) => {
    const [tutorialState, setTutorialState] = useState(false);
    const [grantedState, setGrantedState] = useState(false);
    const { triggerYM } = useYM();

    const triggerYMCamera = useCallback(() => {
        // события должны быть вызваны один раз
        const goalsToTrigger: Record<CameraGoalType, boolean> = {
            [GoalType.CONFERENCE_PAGE_CAM_PERMISSION]: true,
            [GoalType.CONFERENCE_PAGE_CAM_PERMISSION_SUCCESS]: true,
        };

        return (goal: CameraGoalType) => {
            if (goalsToTrigger[goal]) {
                triggerYM({ goal });
                goalsToTrigger[goal] = false;
            }
        };
    }, []);

    const contextValue = useMemo<IContext>(() => {
        return {
            granted: grantedState,
            setGrantedState,
            setTutorialState,
            triggerYMCamera: triggerYMCamera(),
            tutorialState,
        };
    }, [grantedState, tutorialState]);

    useEffect(() => {
        getDevicesPermissionMap(['camera']).then(({ camera }) => setGrantedState(!!camera));
    }, []);

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default CameraPermissionContext;
